import { IAppSettings } from '../../config/interfaces/iapp-settings.interface';

/**
 * Hosts that receive our credentials but are not described by `api` in app settings.
 *
 * The APIM hostnames are here rather than derived because `api.apim` names one environment's
 * gateway, while a build can legitimately talk to the developer portal variant or, locally, to the
 * self-hosted gateway on :8080. `services.ai.azure.com` is Azure AI, which takes the same bearer.
 */
const ADDITIONAL_CREDENTIALED_HOSTS: readonly string[] = [
	'mylanguagebird-staging-apim.azure-api.net',
	'mylanguagebird-staging-apim.developer.azure-api.net',
	'mylanguagebird-prod-apim.azure-api.net',
	'mylanguagebird-prod-apim.developer.azure-api.net',
	'services.ai.azure.com',
	'localhost:8080' // Local APIM self-hosted gateway
];

/**
 * Whether a request is bound for one of our own APIs and may therefore carry credentials.
 *
 * <b>Matches on host, not on substring.</b> The previous rule accepted any URL containing the text
 * `/api`, which is true of our own monolith — `https://my.languagebird.com/api` — and equally true
 * of `https://any-third-party.example.com/api/collect`. Every such request was being handed the
 * bearer token, and would have been handed the act-as header too. The `/api` entry could not simply
 * be deleted, because in every environment it is the only thing that matched the monolith at all:
 * its host appears nowhere else in the allowlist. Deriving the hosts from the configured API URLs
 * keeps the monolith matched while making a third-party host stop matching.
 *
 * Relative URLs are same-origin by definition, so the `/api` path test is safe for those and is
 * kept, which also leaves asset requests uncredentialed as before.
 */
export function isInterceptedUrl(url: string, settings: IAppSettings | null): boolean {
	if (!isAbsolute(url)) {
		return url.startsWith('/api');
	}

	const host = hostOf(url);
	if (host === null) {
		return false;
	}

	return credentialedHosts(settings).has(host);
}

/** The set of hosts we authenticate to: whatever app settings configured, plus the fixed few. */
function credentialedHosts(settings: IAppSettings | null): Set<string> {
	const hosts = new Set<string>(ADDITIONAL_CREDENTIALED_HOSTS);

	// Every configured API base, not just mylb/apim by name: a new microservice key added to
	// app-settings should be reachable without also having to be remembered here.
	for (const base of Object.values(settings?.api ?? {})) {
		const host = hostOf(base);
		if (host !== null) {
			hosts.add(host);
		}
	}

	return hosts;
}

function isAbsolute(url: string): boolean {
	return /^[a-z][a-z0-9+.-]*:\/\//i.test(url) || url.startsWith('//');
}

/**
 * Host and port, or null when the value is not a parseable ABSOLUTE URL.
 *
 * The absolute test is load-bearing rather than tidiness: `api.mock` is `./assets/mock`, and
 * resolving a relative value against any base would yield that base's host and quietly add it to
 * the credentialed set.
 */
function hostOf(url: string): string | null {
	if (!isAbsolute(url)) {
		return null;
	}

	try {
		return new URL(url.startsWith('//') ? `https:${url}` : url).host || null;
	} catch {
		return null;
	}
}
